import { useTranslation } from 'react-i18next'
import {
  Shield,
  Leaf,
  Globe,
  TreeDeciduous,
  Users,
  Building2,
  Truck,
  Download,
  FileText,
  CheckCircle2,
  Clock,
  TrendingUp,
} from 'lucide-react'
import { motion } from 'framer-motion'

const ease = [0.16, 1, 0.3, 1] as [number, number, number, number]

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.7, ease },
  }),
}

const pillars = [
  { key: 'environment', icon: Leaf, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { key: 'social', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
  { key: 'governance', icon: Building2, color: 'text-violet-600', bg: 'bg-violet-50' },
  { key: 'supply', icon: Truck, color: 'text-amber-600', bg: 'bg-amber-50' },
]

const certifications = [
  { key: 'brc', icon: Shield },
  { key: 'fairtrade', icon: Globe },
  { key: 'ecovadis', icon: Leaf },
  { key: 'rainforest', icon: TreeDeciduous },
]

const documents = [
  { key: 'integratedPolicy', icon: FileText },
  { key: 'sustainabilityReport', icon: FileText },
  { key: 'ethicalCode', icon: FileText },
]

const commitments = [
  { key: 'environmental', icon: TrendingUp },
  { key: 'packaging', icon: TrendingUp },
  { key: 'training', icon: CheckCircle2 },
  { key: 'safety', icon: CheckCircle2 },
  { key: 'transparency', icon: CheckCircle2 },
  { key: 'whistleblowing', icon: CheckCircle2 },
]

function getStatusIcon(status: string) {
  if (status.toLowerCase().includes('attivo') || status.toLowerCase().includes('active')) return CheckCircle2
  if (status.toLowerCase().includes('priorit')) return CheckCircle2
  if (status.toLowerCase().includes('pubbl') || status.toLowerCase().includes('published')) return CheckCircle2
  return Clock
}

export function SustainabilityPage() {
  const { t } = useTranslation('common')

  return (
    <>
      {/* 1. Intro Statement */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-0.5 bg-brand mx-auto mb-10" />
          <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-neutral-900 leading-snug mb-6">
            {t('sustainability.intro.tagline')}
          </p>
          <p className="text-lg md:text-xl text-neutral-600 max-w-2xl mx-auto">
            {t('sustainability.intro.subtitle')}
          </p>
          <div className="w-16 h-0.5 bg-brand mx-auto mt-10" />
        </div>
      </section>

      {/* 2. Pillars */}
      <section className="section-padding bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease }}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-3 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-4">
              <span className="w-8 h-px bg-brand" />
              {t('sustainability.pillars.label')}
              <span className="w-8 h-px bg-brand" />
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900">
              {t('sustainability.pillars.title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {pillars.map((pillar, i) => (
              <motion.div
                key={pillar.key}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className={`w-14 h-14 rounded-xl ${pillar.bg} flex items-center justify-center mb-5`}>
                  <pillar.icon className={`w-7 h-7 ${pillar.color}`} />
                </div>
                <h3 className="text-xl font-heading font-semibold text-neutral-900 mb-3">
                  {t(`sustainability.pillars.${pillar.key}.title`)}
                </h3>
                <p className="text-neutral-600 leading-relaxed">
                  {t(`sustainability.pillars.${pillar.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Image + Context */}
      <section className="bg-neutral-900 text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, ease }}
              className="relative"
            >
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="/images/sustainability/protocolli.jpg"
                  alt="Produzione responsabile Bonifanti"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/30 to-transparent pointer-events-none" />
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.6, ease }}
                className="absolute top-6 right-4 md:top-8 md:right-2 bg-brand text-neutral-900 px-4 py-2 rounded-full font-heading font-bold text-sm shadow-lg"
              >
                {t('sustainability.esgBadge')}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, ease }}
            >
              <span className="inline-flex items-center gap-2 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-6">
                <span className="w-8 h-px bg-brand" />
                {t('sustainability.commitment.label')}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold leading-tight mb-8 text-white">
                {t('sustainability.commitment.title')}
              </h2>
              <div className="space-y-4">
                {commitments.map((item, i) => {
                  const status = t(`sustainability.commitment.${item.key}.status`)
                  const StatusIcon = getStatusIcon(status)
                  return (
                    <motion.div
                      key={item.key}
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + i * 0.1, duration: 0.5, ease }}
                      className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-5 py-4"
                    >
                      <span className="text-white font-medium">
                        {t(`sustainability.commitment.${item.key}.title`)}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-brand text-sm font-medium">
                        <StatusIcon className="w-4 h-4" />
                        {status}
                      </span>
                    </motion.div>
                  )
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. Certifications */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease }}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-3 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-4">
              <span className="w-8 h-px bg-brand" />
              {t('sustainability.certifications.label')}
              <span className="w-8 h-px bg-brand" />
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-4">
              {t('sustainability.certifications.title')}
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              {t('sustainability.certifications.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {certifications.map((cert, i) => (
              <motion.div
                key={cert.key}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="group bg-neutral-50 rounded-2xl p-8 md:p-10 hover:bg-white hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-xl bg-brand/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand/20 transition-colors">
                    <cert.icon className="w-7 h-7 text-brand" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-neutral-900 mb-2">
                      {t(`sustainability.certifications.${cert.key}.title`)}
                    </h3>
                    <p className="text-neutral-600 leading-relaxed">
                      {t(`sustainability.certifications.${cert.key}.description`)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Documents */}
      <section className="section-padding bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease }}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-3 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-4">
              <span className="w-8 h-px bg-brand" />
              {t('sustainability.policies.label')}
              <span className="w-8 h-px bg-brand" />
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-4">
              {t('sustainability.policies.title')}
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              {t('sustainability.policies.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {documents.map((doc, i) => (
              <motion.div
                key={doc.key}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-5">
                  <doc.icon className="w-7 h-7 text-brand" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-neutral-900 mb-2">
                  {t(`sustainability.policies.${doc.key}.title`)}
                </h3>
                <p className="text-sm text-neutral-600 mb-4">
                  {t(`sustainability.policies.${doc.key}.description`)}
                </p>
                <span className="inline-block text-xs font-medium text-neutral-400 bg-neutral-100 px-3 py-1 rounded-full mb-5">
                  {t(`sustainability.policies.${doc.key}.year`)}
                </span>
                <div>
                  <a
                    href={`/documents/${doc.key === 'sustainabilityReport' ? 'report-sostenibilita' : doc.key === 'integratedPolicy' ? 'politica-integrata' : 'codice-etico'}.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-brand font-medium text-sm hover:text-brand-dark transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    {t('sustainability.downloadPdf')}
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </>
  )
}
